import { Component, Inject, Input, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { LayerApi } from '../../../api/layer-api';
import { UserApi } from '../../../api/user-api';
import { environment } from '../../../environment';
import { IAuthenticationService } from '../../../services/interfaces/IAuthenticationService';
import { LayerService } from '../../../services/layer.service';
import { LayerCatalogService } from '../../../services/layerCatalog.service';
import { IUserFolder, LayerTypeId, MapCoreLayer, UserFolderCategory } from '../../../types';
import { BaseComponent } from '../../base.component';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-save-layer',
    styleUrls: ['../../sharedStyles/base.scss', './save-layer.component.scss'],
    templateUrl: './save-layer.component.html'
})
export class SaveLayerComponent extends BaseComponent {
    @Input() layerId;
    @Input() enableFolders: boolean = false;
    name: string;
    description: string;
    windowRef;
    userId = null;
    esriLayer: MapCoreLayer;
    createNewFolder = false;
    newFolder: IUserFolder = null;
    parentFolders: IUserFolder[] = [];
    parentFolderId: number = 0;
    appId = environment.appId;
    userSubscriber: any;
    layerType: UserFolderCategory;
    createNewLayer: boolean = true;
    disableCreateLayer: boolean = true;
    isLoading: boolean = false;

    constructor(
        @Inject('authenticationService') public authService: IAuthenticationService,
        private readonly layerService: LayerService,
        private readonly layerCatalogService: LayerCatalogService,
        private readonly layerApi: LayerApi,
        private readonly userApi: UserApi,
        _vcr: ViewContainerRef
    ) {
        super(_vcr);
        this.getClosestAncestor('mc-window').then((window: any) => {
            if (window) {
                this.windowRef = window;
                this.windowRef.addEventListener('openChange', open => {
                    if (open.detail) { // detail is value prop sent by angular event
                        this.windowOpened();
                    } else {
                        this.windowClosed();
                    }
                });
            } else {
                this.windowOpened();
            }
        });
    }

    async windowOpened() {
        this.isLoading = true;
        this.createNewLayer = true;
        this.disableCreateLayer = true;

        if (!this.userSubscriber) {
            this.userSubscriber = await this.authService.GetCurrentUser().subscribe(user => {
                if (user && user.id) {
                    this.userId = user.id;
                } else {
                    this.userId = null;
                }
            });
        }

        this.esriLayer = this.layerService.getEsriLayerByUniqueId(this.layerId);
        this.name = this.esriLayer.mapcoreProps.name;
        this.description = this.esriLayer.mapcoreProps.description;
        this.layerType = (<any>this.esriLayer).type === 'graphics' ? 'drawing-layers' : 'layers';
        this.parentFolders = (await this.userApi.queryUserFolders(this.userId, environment.appId, this.layerType) as IUserFolder[]);
        this.parentFolders = this.parentFolders.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
        this.parentFolders.unshift({ name: 'No parent folder', id: 0 });

        const layers = this.esriLayer.mapcoreProps.idFromSavedLayer ? await this.layerApi.getUserLayers(this.esriLayer.mapcoreProps.idFromSavedLayer) : [];
        const isLayerOwner = layers && layers[0] && this.isUserOwnerOfLayer(layers[0]);
        const canUpdateLayer = this.esriLayer.mapcoreProps.typeId !== LayerTypeId.Map && isLayerOwner;

        if (canUpdateLayer) {
            this.createNewLayer = false;
            this.disableCreateLayer = false;
        } else {
            this.name = this.name + ' - Copy';
        }

        const userLayers = await this.layerCatalogService.getUserLayerCatalog(this.userId);
        const userLayerIds: number[] = userLayers.map(x => x.layer.id);

        // If the layer is a user layer for to this user, then update the selected parent folder if needed
        if (userLayerIds.includes(this.esriLayer.mapcoreProps.idFromSavedLayer)) {
            const userLayerFolders: {
                applicationId: number; id: number; layers: {id: number}[]; name: string; parentUserFolderId: number; }[]
                = (await this.layerService.getUserLayersFolders(this.userId, environment.appId))[0].folders;

            let existingLayersFolderId = 0;
            for (const folder of userLayerFolders) {
                for (const layer of folder.layers) {
                    if (layer.id === this.esriLayer.mapcoreProps.idFromSavedLayer) {
                        existingLayersFolderId = folder.id;
                        break;
                    }
                }
                if (existingLayersFolderId !== 0) { break; }
            }
            this.parentFolderId = existingLayersFolderId;
        }

        this.isLoading = false;
    }

    closeWindow() {
        this.windowRef.close();
    }

    isNewFolder() {
        this.createNewFolder = this.createNewFolder ? false : true;
        if (this.createNewFolder) {
            this.newFolder = { name: '', parentUserFolderId: null, userId: this.userId, applicationId: environment.appId, category: this.layerType };
        } else {
            this.newFolder = undefined;
        }

    }

    parentFolderSelected(value: number) {
        if (this.createNewFolder) {
            this.newFolder.parentUserFolderId = value;
        } else {
            this.esriLayer.mapcoreProps.parentId = value;
        }
    }

    async saveLayer() {
        const layer = this.layerService.getLayerForSave(this.esriLayer, this.name, this.description, LayerTypeId.User, this.createNewLayer, false);
        if (layer && this.userId) {
            if (this.createNewFolder) {
                this.newFolder.parentUserFolderId = this.parentFolderId;
                const createdFolder = await this.userApi.createNewUserFolder(this.newFolder);
                this.parentFolderId = createdFolder.id;
            }

            const newLayerId = await this.layerApi.saveLayer(layer, this.userId);

            if (this.createNewLayer) {
                this.esriLayer.mapcoreProps.idFromSavedLayer = newLayerId;
            }

            this.esriLayer.mapcoreProps.name = this.name;
            this.esriLayer.mapcoreProps.description = this.description;

            if (this.parentFolderId) {
                await this.layerService.saveLayerToUserFolder(newLayerId, this.parentFolderId);
            }

            this.layerService.layersRefreshed.emit();
        }
        this.closeWindow();
    }

    windowClosed() {
        this.esriLayer = null;
        this.createNewFolder = false;
        this.newFolder = undefined;
        this.parentFolders = [];
    }

    isUserOwnerOfLayer(layer): boolean {
        if (layer.userLayers) {
            const ownerIndex = layer.userLayers.findIndex(m => m.accessLevel === 'OWNER');
            if (layer.userLayers[ownerIndex] && layer.userLayers[ownerIndex].userId === this.userId) {
                return true;
            }
        }
        return false;
    }

    folderTrack(_index: number, item: IUserFolder) {
        return item.id;
    }
}
